import React, { useRef, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

const EMPTY = {
    nombre: '',
    telefono: '',
    email: '',
    ciudad: '',
    // Honeypot: oculto para humanos. Si llega con algo, es un bot.
    campo_extra: ''
};

/**
 * Formulario de registro. Se monta dos veces en la landing (hero y cierre),
 * asi que cada instancia necesita su propio `instanceId`: sin el, los `id`
 * del honeypot se repetirian en el DOM y el `htmlFor` apuntaria al primero.
 *
 * `variant` solo cambia el aspecto: "hero" sobre la foto, "solid" sobre fondo claro.
 */
function RegistrationForm({ instanceId = 'hero', variant = 'hero', title, subtitle }) {
    const [formData, setFormData] = useState(EMPTY);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Marca de tiempo para descartar envios instantaneos (antispam).
    const formLoadedAt = useRef(Date.now());

    const honeypotId = `campo_extra_${instanceId}`;

    const handleChange = (e) => {
        const { name, value } = e.target;
        // El telefono solo admite digitos; el codigo de pais lo pone el backend.
        const nextValue = name === 'telefono' ? value.replace(/\D/g, '') : value;

        // Actualizacion funcional: no depende del `formData` capturado en el
        // render actual.
        setFormData((prev) => ({ ...prev, [name]: nextValue }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        const sanitizedPhone = formData.telefono.replace(/\D/g, '');
        if (sanitizedPhone.length !== 10) {
            setError('Ingresa tu número de celular (10 dígitos) sin el código de país.');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await fetch('/api/register.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    telefono: sanitizedPhone,
                    formLoadedAt: formLoadedAt.current
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || 'Error al enviar el formulario');
            }

            setSuccess(data.message);

            // /gracias lo lee para reportar la conversion con la ciudad a Meta
            // y a GA4, y para armar el archivo de calendario.
            try {
                window.sessionStorage.setItem('pc_registro_ciudad', formData.ciudad);
            } catch (storageError) {
                // sessionStorage bloqueado: el registro igual se completo.
            }

            setFormData(EMPTY);
            window.location.href = '/gracias';
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`form form--${variant}`}>
            <Form onSubmit={handleSubmit}>
                {title && <p className="form__title">{title}</p>}
                {subtitle && <p className="form__subtitle">{subtitle}</p>}

                <Form.Group className="mb-3">
                    <Form.Label>Nombre completo</Form.Label>
                    <Form.Control
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        autoComplete="name"
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>WhatsApp</Form.Label>
                    <Form.Control
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="Número de celular (10 dígitos)"
                        autoComplete="tel-national"
                        inputMode="numeric"
                        required
                    />
                    <span className="small">Ingresa tu número sin el código de país.</span>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Correo electrónico</Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Ciudad a donde asistirás</Form.Label>
                    <Form.Select
                        name="ciudad"
                        value={formData.ciudad}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Selecciona una ciudad</option>
                        <option value="cdmx">Ciudad de México (6 de octubre)</option>
                        <option value="guadalajara">Guadalajara (8 de octubre)</option>
                        <option value="monterrey">Monterrey (10 de octubre)</option>
                    </Form.Select>
                </Form.Group>

                {/* Honeypot. Fuera de pantalla en vez de `display:none`: hay
                    bots que se saltan los campos ocultos por display. El
                    nombre es neutro a proposito — con `website` o `url` los
                    gestores de contraseñas lo autocompletan y tumbarian
                    registros legitimos. */}
                <div className="hp" aria-hidden="true">
                    <label htmlFor={honeypotId}>No llenes este campo</label>
                    <input
                        type="text"
                        id={honeypotId}
                        name="campo_extra"
                        value={formData.campo_extra}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                    />
                </div>

                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {success && <div className="alert alert-success" role="alert">{success}</div>}
                {isSubmitting && (
                    <div className="alert alert-info" role="alert">
                        Estamos procesando tu registro y enviándote un correo con la información. Un momento por favor...
                    </div>
                )}

                <Button variant="primary" type="submit" className="button" disabled={isSubmitting}>
                    {isSubmitting ? 'Un momento...' : 'Quiero mi cupo gratis'}
                </Button>

                <span className="k">
                    Al enviar tu registro aceptas la{" "}
                    <Link to="/privacidad">
                        política de privacidad y de tratamiento de datos personales
                    </Link>.
                </span>
            </Form>
        </div>
    );
}

export default RegistrationForm;
